// Generic offline-durable photo-upload queue (field-parity follow-up).
//
// Maintenance work-order photos and housekeeping turn photos are plain multipart
// POSTs that simply fail in a dead zone — losing a tech's proof photo. This
// persists the file blob + target endpoint + any extra form fields to IndexedDB
// and replays them when connectivity returns, so nothing is dropped. Inspections
// has its own dedicated frame queue (lib/captureQueue); this serves every other
// field photo. The global fetch interceptor in main.ts attaches the Supabase
// bearer token, so a replayed upload is authenticated exactly like the live one.

const DB_NAME = 'surge-uploads';
const STORE = 'uploads';
const VERSION = 1;

export interface QueuedUpload {
  id?: number;
  url: string;
  fileField: string;
  name: string;
  blob: Blob;
  fields: Record<string, string>;
  label: string;
  queuedAt: number;
}

function available(): boolean { return typeof indexedDB !== 'undefined'; }

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  return openDb().then(db => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => db.close();
  }));
}

/** Persist one photo upload (endpoint + fields + blob) for later replay. */
export async function queueUpload(url: string, blob: Blob, name: string, opts: { fileField?: string; fields?: Record<string, string>; label?: string } = {}): Promise<void> {
  if (!available()) throw new Error('offline storage unavailable in this browser');
  const rec: QueuedUpload = {
    url, blob, name, fileField: opts.fileField || 'photo', fields: opts.fields || {}, label: opts.label || '', queuedAt: Date.now(),
  };
  await tx('readwrite', store => store.add(rec));
}

export async function pendingUploadCount(): Promise<number> {
  if (!available()) return 0;
  try { return await tx<number>('readonly', store => store.count()); } catch { return 0; }
}

async function listAll(): Promise<QueuedUpload[]> {
  if (!available()) return [];
  try { return await tx<QueuedUpload[]>('readonly', store => store.getAll()); } catch { return []; }
}

async function remove(id: number): Promise<void> { await tx('readwrite', store => store.delete(id)); }

// 'ok' = uploaded; 'drop' = the target record is GONE (404 — the photo has no
// home); 'retry' = anything else. R38: the old rule dropped on ANY 4xx, which
// permanently deleted a cleaner's proof photos on an expired-session 401
// during the load-time flush. Only a 404 discards now.
async function uploadOne(u: QueuedUpload): Promise<'ok' | 'drop' | 'retry'> {
  try {
    const fd = new FormData();
    for (const [k, v] of Object.entries(u.fields || {})) fd.append(k, v);
    fd.append(u.fileField || 'photo', u.blob, u.name);
    const r = await fetch(u.url, { method: 'POST', body: fd });
    if (r.ok) return 'ok';
    return r.status === 404 ? 'drop' : 'retry';
  } catch { return 'retry'; }
}

/** Replay queued uploads in order; stop at the first network/server failure. */
let flushing = false;   // R38: 'online' + 'load' can overlap — a double flush double-uploads the same rows
export async function flushUploads(): Promise<{ uploaded: number; remaining: number }> {
  if (!available() || flushing) return { uploaded: 0, remaining: 0 };
  flushing = true;
  try {
    const items = await listAll();
    let uploaded = 0;
    for (const u of items) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) break;
      const res = await uploadOne(u);
      if (res === 'retry') break;
      if (u.id != null) await remove(u.id);
      if (res === 'ok') uploaded++;
    }
    return { uploaded, remaining: await pendingUploadCount() };
  } finally {
    flushing = false;
  }
}
